import { useMemo } from "react";
import { encodeQr, qrSvgPath, type EccLevel } from "../lib/qr";

interface QrCodeProps {
  /** The text the code carries. For enrolment, an otpauth:// URL. */
  value: string;
  /** What a screen reader should say. The value itself is never read out. */
  label: string;
  className?: string;
  ecc?: EccLevel;
}

/**
 * A QR code as inline SVG.
 *
 * **Always dark on white, in both worlds.** The invite screen is the ink
 * world — near-black canvas, near-white text — and a QR drawn in those
 * colours is an inverted symbol. Plenty of scanners read one; plenty of
 * phone cameras, in a hurry, in bad light, do not, and the person holding the
 * phone has no way to tell which problem they have. So the two colours here
 * are literals rather than theme tokens, and the white is part of the symbol
 * rather than a background the layout could later change.
 *
 * Rendered as SVG and not a canvas or an image: it stays sharp at any size
 * and when printed, needs no ref or effect, and survives being rendered
 * before layout settles.
 *
 * If the value is somehow too long to encode, this renders nothing rather
 * than throwing. On the invite screen the key is also shown as text directly
 * below, so a missing QR costs convenience, never access — and a screen that
 * shows someone their only copy of a credential is the last place to risk an
 * exception taking the whole page down.
 */
export function QrCode({ value, label, className = "", ecc = "M" }: QrCodeProps) {
  const code = useMemo(() => {
    try {
      const matrix = encodeQr(value, ecc);
      return { path: qrSvgPath(matrix), extent: matrix.size + 8 };
    } catch {
      return null;
    }
  }, [value, ecc]);

  if (!code) return null;

  return (
    <svg
      viewBox={`0 0 ${code.extent} ${code.extent}`}
      role="img"
      aria-label={label}
      className={`h-auto w-full max-w-[13.5rem] ${className}`.trim()}
      shapeRendering="crispEdges"
    >
      <rect width={code.extent} height={code.extent} fill="#ffffff" />
      <path d={code.path} fill="#111111" />
    </svg>
  );
}
